import axios from 'axios'

// Product feature flags that map to a specific redirect URI in config.redirectUriByType
const PRODUCT_FLAGS = ['rate_ai', 'spot_quote', 'allocation_ai', 'contract_ai', 'bid_tender', 'lane_ai']

const UAM_LOOKUP_MAX_RETRIES = 3

// Maps the UAM `defaultApp` value to a product flag key used in config.redirectUriByType
const DEFAULT_APP_TO_REDIRECT_KEY = {
  allocationai: 'allocation_ai',
  contractai: 'contract_ai',
  spotquotes: 'spot_quote',
  bidtender: 'bid_tender',
  laneai: 'lane_ai'
}

async function resolveBidTenderRedirect (orgId, config, accessToken) {
  const { data } = await axios.get(`${config.uamUri}/api/external/v1/organisations/${orgId}`, { headers: { Authorization: `Bearer ${accessToken}` } })
  const slug = data.slug
  return `https://${slug}.${config.redirectUriByType.bid_tender}`
}

// Determines the post-login redirect URI for a user based on their enabled products
// and, when multiple products are enabled, their UAM defaultApp preference.
// Falls back to the rate_ai redirect URI if anything is unresolvable.
export default async function resolveRedirectUri (decodedToken, config, accessToken) {
  const fallback = config.redirectUriByType.rate_ai
  console.log('[resolveRedirectUri] starting', { userId: decodedToken.id, fallback })

  try {
    // Collect which product flags are enabled for this user from the access token
    const enabledFlags = PRODUCT_FLAGS.filter(flag => decodedToken[flag])
    console.log('[resolveRedirectUri] enabled flags:', enabledFlags)

    if (enabledFlags.length === 0) {
      // No recognized product flags â use the instance entrypoint or the global fallback
      const redirect = decodedToken.instance_entrypoint || fallback
      console.log('[resolveRedirectUri] no flags enabled, using instance_entrypoint or fallback:', redirect)
      return redirect
    }

    if (enabledFlags.length === 1) {
      if (enabledFlags[0] === 'bid_tender') {
        const redirect = await resolveBidTenderRedirect(decodedToken.organization_id, config, accessToken)
        console.log('[resolveRedirectUri] single flag "bid_tender", redirecting to:', redirect)
        return redirect
      }
      // Exactly one product â route directly to its URI
      const redirect = config.redirectUriByType[enabledFlags[0]] || decodedToken.instance_entrypoint || fallback
      console.log(`[resolveRedirectUri] single flag "${enabledFlags[0]}", redirecting to:`, redirect)
      return redirect
    }

    // Multiple products enabled â check UAM for user's defaultApp preference
    console.log('[resolveRedirectUri] multiple flags enabled, fetching UAM defaultApp for user:', decodedToken.id)
    try {
      const userId = decodedToken.id
      let uamUser = null
      for (let attempt = 1; attempt <= UAM_LOOKUP_MAX_RETRIES; attempt++) {
        try {
          const { data } = await axios.get(`${config.uamUri}/api/users/${userId}`, { headers: { Authorization: `Bearer ${accessToken}` } })
          uamUser = data
          console.log('[resolveRedirectUri] UAM lookup succeeded on attempt', attempt, { defaultApp: uamUser.defaultApp })
          break
        } catch (retryErr) {
          if (attempt === 3) throw retryErr
          console.log(`UAM lookup attempt ${attempt} failed, retrying...`, retryErr)
        }
      }
      if (uamUser && uamUser.defaultApp) {
        const key = DEFAULT_APP_TO_REDIRECT_KEY[uamUser.defaultApp]
        if (key === 'bid_tender') {
          const redirect = await resolveBidTenderRedirect(decodedToken.organization_id, config, accessToken)
          console.log('[resolveRedirectUri] resolved via UAM defaultApp bid_tender:', { defaultApp: uamUser.defaultApp, redirect })
          return redirect
        }
        const redirect = config.redirectUriByType[key] || fallback
        console.log('[resolveRedirectUri] resolved via UAM defaultApp:', { defaultApp: uamUser.defaultApp, key, redirect })
        return redirect
      }
      console.log('[resolveRedirectUri] UAM returned no defaultApp, falling back to:', fallback)
    } catch (uamErr) {
      console.log('UAM lookup failed, falling back to rate_ai', uamErr)
    }

    // UAM returned no defaultApp or was unreachable â use the global fallback
    return fallback
  } catch (err) {
    console.log('resolveRedirectUri failed, falling back', err)
    return fallback
  }
}
